/**
 * V2 Screening Data Collection Sync Utilities
 * -------------------------------------------
 * Bidirectional sync between V2 screening data collection answers and intake form_data.
 *
 * - buildSyncedFieldsFromV2: V2 dataCollectionAnswers → intake form_data patch
 * - buildV2FieldsFromIntake: intake form_data → V2 dataCollectionAnswers (for pre-fill)
 *
 * Only syncs fields that have a valid target in the intake form_data structure.
 */

import { IntakeFormData as IntakeFormDataBackend } from '@/services/types/intake-types';

import { DataCollectionAnswer } from '../types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type SyncStrategy = 'replace' | 'append' | 'merge';

export type SyncSection =
  | 'incident_info'
  | 'insurance'
  | 'personal_info'
  | 'medical_wage_history';

export interface SyncMapping {
  /** The V2 data collection question/sub-question ID */
  v2FieldId: string;
  /** Which section of form_data to sync to */
  intakeSection: SyncSection;
  /** The path within the section (supports dot notation for nested fields) */
  intakePath: string;
  /** How to merge the value (replace, append, or merge for objects) */
  strategy: SyncStrategy;
  /** Optional transform function for value conversion */
  transform?: (value: string | undefined) => string | boolean | undefined;
}

// ---------------------------------------------------------------------------
// Transform Functions
// ---------------------------------------------------------------------------

/** Convert yes/no/unknown to boolean (yes = true, no/unknown = false) */
const yesNoToBoolean = (value: string | undefined): boolean | undefined => {
  if (!value) return undefined;
  return value.toLowerCase() === 'yes';
};

/** Keep as string (identity transform) */
const asString = (value: string | undefined): string | undefined => value;

// ---------------------------------------------------------------------------
// MVA Sync Mappings
// ---------------------------------------------------------------------------

export const MVA_SYNC_MAPPINGS: SyncMapping[] = [
  // Personal Info
  {
    v2FieldId: 'personal_info.dob',
    intakeSection: 'personal_info',
    intakePath: 'dob',
    strategy: 'replace',
    transform: asString,
  },
  {
    v2FieldId: 'personal_info.ssn',
    intakeSection: 'personal_info',
    intakePath: 'ssn_last_4',
    strategy: 'replace',
    transform: asString,
  },
  {
    v2FieldId: 'personal_info.address',
    intakeSection: 'personal_info',
    intakePath: 'address',
    strategy: 'replace',
    transform: asString,
  },

  // Incident Details
  {
    v2FieldId: 'accident_date',
    intakeSection: 'incident_info',
    intakePath: 'date',
    strategy: 'replace',
    transform: asString,
  },
  {
    v2FieldId: 'accident_location',
    intakeSection: 'incident_info',
    intakePath: 'location',
    strategy: 'replace',
    transform: asString,
  },
  {
    v2FieldId: 'accident_description',
    intakeSection: 'incident_info',
    intakePath: 'description',
    strategy: 'replace',
    transform: asString,
  },

  // Police Report
  {
    v2FieldId: 'police_report',
    intakeSection: 'incident_info',
    intakePath: 'police_report',
    strategy: 'replace',
    transform: yesNoToBoolean,
  },
  {
    v2FieldId: 'police_report.department',
    intakeSection: 'incident_info',
    intakePath: 'police_dept',
    strategy: 'replace',
    transform: asString,
  },
  {
    v2FieldId: 'police_report.report_number',
    intakeSection: 'incident_info',
    intakePath: 'police_report_number',
    strategy: 'replace',
    transform: asString,
  },

  // At-Fault Insurance (syncs to defendants[0])
  {
    v2FieldId: 'at_fault_insurance.carrier',
    intakeSection: 'insurance',
    intakePath: 'defendants.0.insurance_company_name',
    strategy: 'replace',
    transform: asString,
  },
  {
    v2FieldId: 'at_fault_insurance.policy_number',
    intakeSection: 'insurance',
    intakePath: 'defendants.0.policy_number',
    strategy: 'replace',
    transform: asString,
  },
  {
    v2FieldId: 'at_fault_insurance.claim_number',
    intakeSection: 'insurance',
    intakePath: 'defendants.0.claim_number',
    strategy: 'replace',
    transform: asString,
  },

  // Client Insurance (syncs to pip_insurance when they are the driver)
  {
    v2FieldId: 'client_insurance.carrier',
    intakeSection: 'insurance',
    intakePath: 'pip_insurance.company_name',
    strategy: 'replace',
    transform: asString,
  },
  {
    v2FieldId: 'client_insurance.policy_number',
    intakeSection: 'insurance',
    intakePath: 'pip_insurance.policy_number',
    strategy: 'replace',
    transform: asString,
  },
  {
    v2FieldId: 'client_insurance.claim_number',
    intakeSection: 'insurance',
    intakePath: 'pip_insurance.claim_number',
    strategy: 'replace',
    transform: asString,
  },

  // Health Insurance
  {
    v2FieldId: 'client_health_insurance.carrier',
    intakeSection: 'insurance',
    intakePath: 'health_provider',
    strategy: 'replace',
    transform: asString,
  },
  {
    v2FieldId: 'client_health_insurance.policy_number',
    intakeSection: 'insurance',
    intakePath: 'health_member_id',
    strategy: 'replace',
    transform: asString,
  },
  {
    v2FieldId: 'client_health_insurance.claim_number',
    intakeSection: 'insurance',
    intakePath: 'health_group_id',
    strategy: 'replace',
    transform: asString,
  },
];

// ---------------------------------------------------------------------------
// Premises Liability Sync Mappings
// ---------------------------------------------------------------------------

export const PREMISES_SYNC_MAPPINGS: SyncMapping[] = [
  // Personal Info
  {
    v2FieldId: 'pl_personal_info.dob',
    intakeSection: 'personal_info',
    intakePath: 'dob',
    strategy: 'replace',
    transform: asString,
  },
  {
    v2FieldId: 'pl_personal_info.ssn',
    intakeSection: 'personal_info',
    intakePath: 'ssn_last_4',
    strategy: 'replace',
    transform: asString,
  },
  {
    v2FieldId: 'pl_personal_info.address',
    intakeSection: 'personal_info',
    intakePath: 'address',
    strategy: 'replace',
    transform: asString,
  },

  // Incident Details
  {
    v2FieldId: 'pl_incident_date',
    intakeSection: 'incident_info',
    intakePath: 'date',
    strategy: 'replace',
    transform: asString,
  },
  {
    v2FieldId: 'pl_incident_location',
    intakeSection: 'incident_info',
    intakePath: 'location',
    strategy: 'replace',
    transform: asString,
  },
  {
    v2FieldId: 'pl_incident_description',
    intakeSection: 'incident_info',
    intakePath: 'description',
    strategy: 'replace',
    transform: asString,
  },

  // Property Owner (syncs to defendants[0])
  {
    v2FieldId: 'pl_property_owner.name',
    intakeSection: 'insurance',
    intakePath: 'defendants.0.name',
    strategy: 'replace',
    transform: asString,
  },

  // Health Insurance
  {
    v2FieldId: 'pl_health_insurance.carrier',
    intakeSection: 'insurance',
    intakePath: 'health_provider',
    strategy: 'replace',
    transform: asString,
  },
  {
    v2FieldId: 'pl_health_insurance.policy_number',
    intakeSection: 'insurance',
    intakePath: 'health_member_id',
    strategy: 'replace',
    transform: asString,
  },
  {
    v2FieldId: 'pl_health_insurance.claim_number',
    intakeSection: 'insurance',
    intakePath: 'health_group_id',
    strategy: 'replace',
    transform: asString,
  },
];

// ---------------------------------------------------------------------------
// Helper Functions
// ---------------------------------------------------------------------------

/**
 * Get the appropriate sync mappings for a case type.
 */
export function getSyncMappings(caseType: string | null | undefined): SyncMapping[] {
  const normalizedType = caseType?.toLowerCase().replace(/\s+/g, '_');

  switch (normalizedType) {
    case 'motor_vehicle':
    case 'motor_vehicle_accident':
    case 'auto_accident':
    case 'mva':
      return MVA_SYNC_MAPPINGS;

    case 'premise_liability':
    case 'premises_liability':
      return PREMISES_SYNC_MAPPINGS;

    default:
      // Return empty for unsupported case types
      return [];
  }
}

/**
 * Set a value at a nested path in an object.
 * Supports dot notation (e.g., 'defendants.0.name').
 * Creates intermediate objects/arrays as needed.
 */
function setNestedValue(
  obj: Record<string, unknown>,
  path: string,
  value: unknown
): void {
  const parts = path.split('.');

  // Process all parts except the last one to build the path
  parts.slice(0, -1).reduce<Record<string, unknown>>((current, part, index) => {
    const nextPart = parts[index + 1];
    const isNextIndex = /^\d+$/.test(nextPart);

    if (current[part] === undefined || current[part] === null) {
      current[part] = isNextIndex ? [] : {};
    }

    return current[part] as Record<string, unknown>;
  }, obj);

  // Set the value at the final path
  const lastPart = parts[parts.length - 1];
  const parentPath = parts.slice(0, -1);
  const parent = parentPath.reduce<Record<string, unknown>>(
    (current, part) => current[part] as Record<string, unknown>,
    obj
  );
  parent[lastPart] = value;
}

/**
 * Get a value from a nested path in an object.
 * Supports dot notation (e.g., 'defendants.0.name').
 */
function getNestedValue(obj: Record<string, unknown>, path: string): unknown {
  return path.split('.').reduce<unknown>((current, part) => {
    if (current === undefined || current === null) return undefined;
    return (current as Record<string, unknown>)[part];
  }, obj);
}

// ---------------------------------------------------------------------------
// Main Sync Functions
// ---------------------------------------------------------------------------

/**
 * Build a partial form_data patch from V2 data collection answers.
 *
 * This function maps V2 screening data collection answers to the corresponding
 * intake form_data fields based on the case type.
 *
 * @param dataCollectionAnswers - Record of V2 data collection answers
 * @param caseType - The case type (e.g., 'motor_vehicle', 'premise_liability')
 * @returns Partial IntakeFormData ready for PATCH
 */
export function buildSyncedFieldsFromV2(
  dataCollectionAnswers: Record<string, DataCollectionAnswer>,
  caseType: string | null | undefined
): Partial<IntakeFormDataBackend> {
  const mappings = getSyncMappings(caseType);

  if (mappings.length === 0) {
    return {};
  }

  const patch: Partial<IntakeFormDataBackend> = {};

  // Initialize sections
  const sectionInits: Record<SyncSection, () => void> = {
    personal_info: () => {
      if (!patch.personal_info) patch.personal_info = {} as IntakeFormDataBackend['personal_info'];
    },
    incident_info: () => {
      if (!patch.incident_info) patch.incident_info = {} as IntakeFormDataBackend['incident_info'];
    },
    insurance: () => {
      if (!patch.insurance) patch.insurance = {} as IntakeFormDataBackend['insurance'];
    },
    medical_wage_history: () => {
      if (!patch.medical_wage_history) patch.medical_wage_history = {} as IntakeFormDataBackend['medical_wage_history'];
    },
  };

  // Process each mapping
  mappings.forEach((mapping) => {
    const answer = dataCollectionAnswers[mapping.v2FieldId];
    if (!answer || answer.value === undefined) return;

    // Apply transform
    const transformedValue = mapping.transform
      ? mapping.transform(answer.value)
      : answer.value;

    if (transformedValue === undefined) return;

    // Initialize section if needed
    sectionInits[mapping.intakeSection]();

    // Set the value at the nested path
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const section = patch[mapping.intakeSection] as any;
    setNestedValue(section, mapping.intakePath, transformedValue);
  });

  return patch;
}

/**
 * Build V2 data collection answers from existing intake form_data.
 *
 * This function maps intake form_data fields back to V2 screening data
 * collection answers for pre-filling the data collection screen.
 *
 * @param formData - Existing intake form_data
 * @param caseType - The case type (e.g., 'motor_vehicle', 'premise_liability')
 * @returns Record of V2 data collection answers
 */
export function buildV2FieldsFromIntake(
  formData: Partial<IntakeFormDataBackend> | null | undefined,
  caseType: string | null | undefined
): Record<string, DataCollectionAnswer> {
  if (!formData) return {};

  const mappings = getSyncMappings(caseType);

  if (mappings.length === 0) {
    return {};
  }

  const answers: Record<string, DataCollectionAnswer> = {};

  mappings.forEach((mapping) => {
    const section = formData[mapping.intakeSection];
    if (!section) return;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const value = getNestedValue(section as any, mapping.intakePath);
    if (value === undefined || value === null || value === '') return;

    // Reverse transform for booleans
    let stringValue: string;
    if (typeof value === 'boolean') {
      stringValue = value ? 'yes' : 'no';
    } else {
      stringValue = String(value);
    }

    answers[mapping.v2FieldId] = {
      value: stringValue,
      asked: false,
    };
  });

  return answers;
}

/**
 * Sync medical treatment providers from V2 object_array to intake treatments array.
 *
 * This is a separate function because medical_treatment uses object_array type
 * which needs special handling.
 *
 * @param dataCollectionAnswers - Record of V2 data collection answers
 * @param caseType - The case type
 * @returns Array of treatment objects for medical_wage_history.treatments
 */
export function buildTreatmentsFromV2(
  dataCollectionAnswers: Record<string, DataCollectionAnswer>,
  caseType: string | null | undefined
): Array<{
  provider: string;
  date_of_service: string;
  treatment_received: string;
  still_treating: boolean;
}> {
  const normalizedType = caseType?.toLowerCase().replace(/\s+/g, '_');
  const treatmentFieldId =
    normalizedType === 'premise_liability' || normalizedType === 'premises_liability'
      ? 'pl_medical_treatment'
      : 'medical_treatment';

  const treatmentAnswer = dataCollectionAnswers[treatmentFieldId];
  if (!treatmentAnswer?.arrayValue || !Array.isArray(treatmentAnswer.arrayValue)) {
    return [];
  }

  return treatmentAnswer.arrayValue.map((provider) => ({
    provider: provider.provider_name || '',
    date_of_service: provider.start_date || '',
    treatment_received: provider.details || '',
    still_treating: !provider.end_date,
  }));
}

/**
 * Build V2 medical treatment array from existing intake treatments.
 *
 * @param treatments - Existing treatments array from intake
 * @returns Array of provider objects for V2 data collection
 */
export function buildV2TreatmentsFromIntake(
  treatments: Array<{
    provider?: string;
    date_of_service?: string;
    treatment_received?: string;
    still_treating?: boolean;
  }> | undefined
): Record<string, string>[] {
  if (!treatments || !Array.isArray(treatments)) {
    return [];
  }

  return treatments
    .filter((t) => t.provider)
    .map((t) => ({
      provider_name: t.provider || '',
      treatment_type: '',
      start_date: t.date_of_service || '',
      end_date: t.still_treating ? '' : 'Completed',
      details: t.treatment_received || '',
    }));
}

/**
 * Merge intake treatments with existing V2 treatments.
 *
 * This function syncs treatment data from intake form to V2 screening while
 * PRESERVING the V2 `end_date` field (which has richer data than intake's
 * still_treating boolean).
 *
 * @param intakeTreatments - Treatments from intake form
 * @param existingV2Treatments - Existing V2 treatment array
 * @returns Merged array preserving V2 end_date values
 */
export function mergeIntakeTreatmentsWithV2(
  intakeTreatments: Array<{
    provider?: string;
    date_of_service?: string;
    treatment_received?: string;
    still_treating?: boolean;
  }> | undefined,
  existingV2Treatments: Record<string, string>[] | undefined
): Record<string, string>[] {
  if (!intakeTreatments || !Array.isArray(intakeTreatments)) {
    return existingV2Treatments || [];
  }

  // Build a map of existing V2 treatments by provider name for quick lookup
  const v2ByProvider = new Map<string, Record<string, string>>();
  if (existingV2Treatments) {
    existingV2Treatments.forEach((t) => {
      if (t.provider_name) {
        v2ByProvider.set(t.provider_name.toLowerCase(), t);
      }
    });
  }

  return intakeTreatments
    .filter((t) => t.provider)
    .map((t) => {
      const providerKey = (t.provider || '').toLowerCase();
      const existingV2 = v2ByProvider.get(providerKey);

      return {
        provider_name: t.provider || '',
        treatment_type: existingV2?.treatment_type || '',
        start_date: t.date_of_service || '',
        // PRESERVE V2 end_date - don't overwrite with intake's still_treating conversion
        end_date: existingV2?.end_date ?? '',
        details: t.treatment_received || '',
      };
    });
}
